import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { BiDownArrowAlt } from 'react-icons/bi';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import AccountTypeEnum from '~Api/Investor/AccountTypeEnum';
import IAnnualStatement from '~Api/Investor/IAnnualStatement';
import IInvestor from '~Api/Investor/IInvestor';
import { annualStatementsListAction, currentInvestorGetAction } from '~Investor/actions';
import { currentInvestorAccountAnnualStatementsSelector, currentInvestorSelector } from '~Investor/selectors';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import Pagination from '~UI/Pagination';

export default function AnnualStatements(): ReactElement {
    const [ currentPage, setCurrentPage ] = useState<number>(1);
    const [ perPage ] = useState<number>(3);

    const annualStatements: IDictionary<IAnnualStatement> = useSelector(
        (state: IGlobalState) => currentInvestorAccountAnnualStatementsSelector(state, AccountTypeEnum.IncomeTrust),
    );
    const currentInvestor: IInvestor = useSelector(currentInvestorSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!annualStatements) {
            dispatch(annualStatementsListAction(AccountTypeEnum.IncomeTrust));
        }
    }, []);

    useEffect(() => {
        if (!currentInvestor) {
            dispatch(currentInvestorGetAction());
        }
    }, []);

    const onClickPage: (page: number) => void = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    if (!annualStatements || !currentInvestor) {
        return (
            <div className='content-box'>
                <Spinner animation='border' />
            </div>
        );
    }

    if (_.isEmpty(annualStatements)) {
        return (
            <div className='content-box'>
                <h2>Annual Income Statements</h2>
                <p>No annual income statements are available for your account yet.</p>
            </div>
        );
    }

    const offset: number = (currentPage - 1) * perPage;
    const pageAnnualStatements: IAnnualStatement[] = _.values(annualStatements).slice(offset, offset + perPage);

    const statementsBlock: JSX.Element[] = _.map(_.orderBy(pageAnnualStatements, [ 'year' ], [ 'desc' ]), (annualStatement: IAnnualStatement) => (
        <Button
            href={`${process.env.API_HOST}/investor-annual-statements/${annualStatement.uuid}/download`}
            target='_blank'
        >
            {annualStatement.year} Statement <BiDownArrowAlt />
        </Button>
    ));

    return (
        <div className='content-box'>
            <h2>Annual Income Statements</h2>
            <p>These statements are to assist you in preparing your tax returns.</p>
            {statementsBlock}
            <div className='pagination-block'>
                <Pagination
                    currentPage={currentPage}
                    itemCount={_.values(annualStatements).length}
                    onClickPage={onClickPage}
                    perPage={perPage}
                    showChangePerPage={false}
                />
            </div>
        </div>
    );
}
